import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONFERENCE } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = NAV_LINKS.map((l) => l.href.slice(1));
      let current = "";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); go("#hero"); }}
          className="flex items-baseline gap-1.5 group"
          data-testid="nav-logo"
        >
          <span className={`font-heading text-xl font-bold tracking-tight transition-colors ${scrolled ? "text-ink" : "text-white"}`}>BioInnovate</span>
          <span className="font-mono text-xs font-semibold text-crimson">'26</span>
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className={`text-sm font-medium tracking-wide transition-colors relative ${
                active === l.href.slice(1)
                  ? "text-crimson"
                  : scrolled
                  ? "text-ink/70 hover:text-ink"
                  : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
              {active === l.href.slice(1) && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-crimson" />
              )}
            </button>
          ))}
          <button
            onClick={() => go("#register")}
            data-testid="nav-register-btn"
            className="bg-crimson text-white text-sm font-semibold px-5 py-2.5 hover:bg-ink transition-colors"
          >
            Register
          </button>
        </div>

        <button
          className={`lg:hidden p-2 transition-colors ${scrolled ? "text-ink" : "text-white"}`}
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink/10 px-5 py-4 space-y-1" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-mobile-link-${l.href.slice(1)}`}
              className="block w-full text-left py-3 text-base font-medium text-ink/80 border-b border-ink/5"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#register")}
            data-testid="nav-mobile-register-btn"
            className="w-full bg-crimson text-white font-semibold py-3 mt-3"
          >
            Register Now
          </button>
        </div>
      )}
    </header>
  );
}
